/**
 * Pure logic of the Competition V3 pool search (no network, no React): query parsing, the major-asset rule,
 * GeckoTerminal response parsing, the on-chain verification that decides whether a pool may be shown at all,
 * activity assessment and ranking. server.ts feeds it with on-chain reads; the tests feed it with fixtures.
 */
import { DEXES } from '../config'

export type Address = `0x${string}`
export type Kind = 'V2' | 'V3'

export const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
export const ZERO = '0x0000000000000000000000000000000000000000'
export const lc = (a?: string) => (a ?? '').toLowerCase()
export const MULTICALL3: Address = '0xcA11bde05977b3631167028862bE2a173976CA11'

/** Major assets: fine as the quote side of a pool, never the project token of a competition. */
export const MAJOR_TOKENS: Record<string, string> = {
  '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 'WBNB',
  '0x55d398326f99059ff775485246999027b3197955': 'USDT',
  '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d': 'USDC',
  '0xe9e7cea3dedca5984780bafc599bd69add087d56': 'BUSD',
  '0x7130d2a12b9bcbfae4f2634d864a1ee1ce3ead9c': 'BTCB',
  '0x2170ed0880ac9a755fd29b2688956bd959f933f8': 'ETH',
}
export const MAJOR_SYMBOLS = new Set(['WBNB', 'BNB', 'USDT', 'USDC', 'BUSD', 'BTCB', 'ETH'])
export const isMajorToken = (a?: string) => lc(a) in MAJOR_TOKENS
export const isMajorSymbol = (s?: string) => MAJOR_SYMBOLS.has((s ?? '').trim().toUpperCase())
export const MAJOR_LIST_TEXT = 'WBNB, USDT, USDC, BUSD, BTCB or ETH'

export type QuoteToken = { address: Address; symbol: string; decimals: number; stable?: boolean }
/** The other side we look for when discovering pools on-chain (the app's own lists, all 18 decimals on BSC). */
export const QUOTE_TOKENS: QuoteToken[] = [
  { address: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', symbol: 'WBNB', decimals: 18 },
  { address: '0x55d398326f99059fF775485246999027B3197955', symbol: 'USDT', decimals: 18, stable: true },
  { address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d', symbol: 'USDC', decimals: 18, stable: true },
  { address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', symbol: 'BUSD', decimals: 18, stable: true },
  { address: '0x76e112203eF59D445452ef7556386dD2DF3Ed914', symbol: 'CADINU', decimals: 18 },
  { address: '0x6e64fCF15Be3eB71C3d42AcF44D85bB119b2D98b', symbol: 'CBON', decimals: 18 },
  { address: '0x0E09FaBB73Bd3Ade0a17ECC321fD13a19e81cE82', symbol: 'CAKE', decimals: 18 },
  { address: '0x7130d2A12B9BCbFAe4f2634d864A1Ee1Ce3Ead9c', symbol: 'BTCB', decimals: 18 },
  { address: '0x2170Ed0880ac9A755fd29B2688956BD959F933F8', symbol: 'ETH', decimals: 18 },
]

/** Fee tiers each V3 factory has enabled (feeAmountTickSpacing != 0, read on-chain 2026-09-30). Uniswap V3 has 3000, the others 2500. */
export const V3_FEE_TIERS: Record<string, number[]> = {
  // CADINUdex V3; 5000 = the CAD1 tier (0.5 %, tick spacing 100, enabled 2026-10-09 and whitelisted for the CAD1 launcher only)
  [lc('0x2cb62aEBa8eB609020129983A5141DB883abca85')]: [100, 500, 2500, 5000, 10000, 30000],
  [lc('0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865')]: [100, 500, 2500, 10000], // PancakeSwap V3
  [lc('0xdB1d10011AD0Ff90774D0C6Bb92e5C5c8b4461F7')]: [100, 500, 3000, 10000], // Uniswap V3
}
export const feePercent = (fee: number) => `${(fee / 10000).toLocaleString('en-US', { maximumFractionDigits: 4 })}%`

/** GeckoTerminal dex id -> our exchange name. Only a hint to pre-filter candidates; the on-chain check decides. */
export const GECKO_DEX_TO_NAME: Record<string, string> = {
  'pancakeswap-v3-bsc': 'PancakeSwap V3',
  pancakeswap_v2: 'PancakeSwap V2',
  'uniswap-bsc': 'Uniswap V3',
  'uniswap-v3-bsc': 'Uniswap V3',
  'uniswap-v2-bsc': 'Uniswap V2',
  biswap: 'Biswap',
  bakeryswap: 'BakerySwap',
}
export const geckoDexToFactory = (id?: string) => {
  const name = GECKO_DEX_TO_NAME[(id ?? '').toLowerCase()]
  return name ? DEXES.find((d) => d.name === name) : undefined
}

// ---------------------------------------------------------------------------------------------- query

export type ParsedQuery =
  | { mode: 'address'; address: Address; key: string }
  | { mode: 'symbol'; symbol: string; key: string }
  | { mode: 'position'; id: string; key: string }
  | { mode: 'invalid'; reason: 'empty' | 'partial_address' | 'bad_characters' | 'too_short' }

export function classifyQuery(raw: string): ParsedQuery {
  const q = (raw ?? '').trim()
  if (!q) return { mode: 'invalid', reason: 'empty' }
  if (/^0x/i.test(q)) {
    return ADDRESS_RE.test(q) ? { mode: 'address', address: q as Address, key: `a:${lc(q)}` } : { mode: 'invalid', reason: 'partial_address' }
  }
  // a CADINUdex V3 position number: "#142", "LP 142", "position 142" or just "142" (digits are never a symbol worth searching)
  const pos = q.match(/^(?:#|lp\s*#?|position\s*#?)?\s*(\d{1,9})$/i)
  if (pos) return { mode: 'position', id: String(Number(pos[1])), key: `p:${Number(pos[1])}` }
  const symbol = q.replace(/^\$/, '').trim()
  if (symbol.length < 2) return { mode: 'invalid', reason: 'too_short' }
  if (!/^[A-Za-z0-9._\- ]{2,24}$/.test(symbol)) return { mode: 'invalid', reason: 'bad_characters' }
  return { mode: 'symbol', symbol, key: `s:${symbol.toLowerCase()}` }
}

export type ValidQuery = Exclude<ParsedQuery, { mode: 'invalid' }>
/** What the client sends as ?q= and the server echoes back as `query`. */
export const canonicalQuery = (p: ValidQuery) => (p.mode === 'address' ? p.address : p.mode === 'symbol' ? p.symbol : `#${p.id}`)
/** CADINUdex V3 NonfungiblePositionManager: positions(id) -> token0, token1, fee of the pool the position lives in. */
export const CADINU_V3_NPM: Address = '0x0C26558A7Bf8be790774fc84De8e5229A4dB5BA1'

// ---------------------------------------------------------------------------------------------- gecko

export type GeckoPool = {
  pool: Address
  dexId: string
  baseToken: Address
  quoteToken: Address
  baseSymbol?: string
  quoteSymbol?: string
  /** token names and https logos as the source reports them (search by name, suggestion rows) */
  baseName?: string
  quoteName?: string
  baseLogo?: string | null
  quoteLogo?: string | null
  liquidityUsd: number | null
  volume24hUsd: number | null
  txs24h: number | null
  market: GeckoMarket
}
/** What GeckoTerminal reports about the pool's base token: prices, FDV, market cap and price change windows (base token, as Gecko reports them). Used by the token pages. */
export type GeckoMarket = {
  baseToken: Address
  basePriceUsd: number | null
  quotePriceUsd: number | null
  fdvUsd: number | null
  marketCapUsd: number | null
  change: { m5: number | null; h1: number | null; h6: number | null; h24: number | null }
}
const num = (x: unknown): number | null => {
  if (x === null || x === undefined || x === '') return null
  const n = Number(x)
  return Number.isFinite(n) ? n : null
}
const tokenAddr = (id?: string) => {
  const a = (id ?? '').replace(/^bsc_/, '')
  return ADDRESS_RE.test(a) ? (a as Address) : undefined
}

const httpsOrNull = (u: unknown) => (typeof u === 'string' && u.startsWith('https://') ? u : null)

/** Keeps only 20-byte pool addresses on BSC (Uniswap V4 "pools" are 32-byte ids and cannot be used). */
export function parseGeckoPools(json: any): GeckoPool[] {
  const included: Record<string, any> = {}
  for (const i of json?.included ?? []) included[`${i.type}:${i.id}`] = i.attributes
  const out: GeckoPool[] = []
  for (const p of json?.data ?? []) {
    const a = p?.attributes
    const addr = a?.address
    if (!addr || !ADDRESS_RE.test(addr)) continue
    const rel = p.relationships ?? {}
    const base = tokenAddr(rel.base_token?.data?.id)
    const quote = tokenAddr(rel.quote_token?.data?.id)
    if (!base || !quote) continue
    const tx = a.transactions?.h24
    out.push({
      pool: addr as Address,
      dexId: String(rel.dex?.data?.id ?? ''),
      baseToken: base,
      quoteToken: quote,
      baseSymbol: included[`token:${rel.base_token?.data?.id}`]?.symbol,
      quoteSymbol: included[`token:${rel.quote_token?.data?.id}`]?.symbol,
      baseName: included[`token:${rel.base_token?.data?.id}`]?.name,
      quoteName: included[`token:${rel.quote_token?.data?.id}`]?.name,
      baseLogo: httpsOrNull(included[`token:${rel.base_token?.data?.id}`]?.image_url),
      quoteLogo: httpsOrNull(included[`token:${rel.quote_token?.data?.id}`]?.image_url),
      liquidityUsd: num(a.reserve_in_usd),
      volume24hUsd: num(a.volume_usd?.h24),
      txs24h: tx ? (num(tx.buys) ?? 0) + (num(tx.sells) ?? 0) : null,
      market: {
        baseToken: base,
        basePriceUsd: num(a.base_token_price_usd),
        quotePriceUsd: num(a.quote_token_price_usd),
        // GeckoTerminal answers 0 for a cap it does not know
        fdvUsd: (num(a.fdv_usd) ?? 0) > 0 ? num(a.fdv_usd) : null,
        marketCapUsd: (num(a.market_cap_usd) ?? 0) > 0 ? num(a.market_cap_usd) : null,
        change: { m5: num(a.price_change_percentage?.m5), h1: num(a.price_change_percentage?.h1), h6: num(a.price_change_percentage?.h6), h24: num(a.price_change_percentage?.h24) },
      },
    })
  }
  return out
}

// ---------------------------------------------------------------------------------------------- DexScreener (fallback)

/**
 * DexScreener's exchange id + version label -> the GeckoTerminal id of the same supported exchange, so a DexScreener pool goes through the
 * same filters and the same on-chain verification. Anything else (PancakeSwap V1, Uniswap V4, other exchanges) is dropped.
 */
export function dexScreenerDexId(dexId: string, labels?: string[]): string | undefined {
  const d = lc(dexId)
  const v = (labels ?? []).map(lc)
  if (d === 'pancakeswap') return v.includes('v3') ? 'pancakeswap-v3-bsc' : v.includes('v1') || v.includes('v4') ? undefined : 'pancakeswap_v2'
  if (d === 'uniswap') return v.includes('v3') ? 'uniswap-v3-bsc' : v.includes('v2') ? 'uniswap-v2-bsc' : undefined
  if (d === 'biswap') return 'biswap'
  if (d === 'bakeryswap') return 'bakeryswap'
  return undefined
}

/** DexScreener /latest/dex/search or /latest/dex/tokens answer -> pools on BSC of the supported exchanges (unverified until verifyPool). */
export function parseDexScreenerPairs(json: any): GeckoPool[] {
  const out: GeckoPool[] = []
  for (const p of json?.pairs ?? []) {
    if (lc(p?.chainId) !== 'bsc' || !ADDRESS_RE.test(p?.pairAddress ?? '')) continue
    const dexId = dexScreenerDexId(String(p.dexId ?? ''), p.labels)
    const base = p.baseToken?.address
    const quote = p.quoteToken?.address
    if (!dexId || !ADDRESS_RE.test(base ?? '') || !ADDRESS_RE.test(quote ?? '')) continue
    const tx = p.txns?.h24
    const pos = (x: unknown) => ((num(x) ?? 0) > 0 ? num(x) : null)
    out.push({
      pool: p.pairAddress as Address,
      dexId,
      baseToken: base as Address,
      quoteToken: quote as Address,
      baseSymbol: p.baseToken?.symbol,
      quoteSymbol: p.quoteToken?.symbol,
      baseName: p.baseToken?.name,
      quoteName: p.quoteToken?.name,
      baseLogo: httpsOrNull(p.info?.imageUrl),
      quoteLogo: null,
      liquidityUsd: num(p.liquidity?.usd),
      volume24hUsd: num(p.volume?.h24),
      txs24h: tx ? (num(tx.buys) ?? 0) + (num(tx.sells) ?? 0) : null,
      market: {
        baseToken: base as Address,
        basePriceUsd: num(p.priceUsd),
        quotePriceUsd: null,
        fdvUsd: pos(p.fdv),
        marketCapUsd: pos(p.marketCap),
        change: { m5: num(p.priceChange?.m5), h1: num(p.priceChange?.h1), h6: num(p.priceChange?.h6), h24: num(p.priceChange?.h24) },
      },
    })
  }
  return out
}

// ---------------------------------------------------------------------------------------------- verification

export type FactoryRule = { name: string; kind: Kind; factory: Address; enabled: boolean; chainKind?: Kind }
export type FactoryRules = Record<string, FactoryRule>
export type PoolReads = { pool: Address; factory?: string; token0?: string; token1?: string; fee?: number; registered?: string }
export type VerifyFail = 'not_a_pool' | 'unsupported_factory' | 'factory_disabled' | 'kind_mismatch' | 'not_registered'
export type Verified = { ok: true; dex: string; kind: Kind; factory: Address; fee: number | null; token0: Address; token1: Address }
export type Rejected = { ok: false; reason: VerifyFail }
export type VerifyResult = Verified | Rejected

/** Builds the lookup from the contract's own answers: supportedFactories(f) and factoryPoolKind(f) (0 = V3, 1 = V2). */
export function buildFactoryRules(chain: Array<{ factory: string; enabled?: boolean; kind?: number }>): FactoryRules {
  const rules: FactoryRules = {}
  for (const d of DEXES) {
    const c = chain.find((x) => lc(x.factory) === lc(d.factory))
    rules[lc(d.factory)] = {
      name: d.name,
      kind: d.kind as Kind,
      factory: d.factory as Address,
      enabled: c?.enabled === true,
      chainKind: c?.kind === 0 ? 'V3' : c?.kind === 1 ? 'V2' : undefined,
    }
  }
  return rules
}

/**
 * A pool is shown (and selectable) only when all of these hold, mirroring what the contract re-checks at creation:
 *  1. pool.factory() is one of the supported factories and the contract still has it enabled;
 *  2. the pool's own kind (V3 when it answers fee(), else V2) equals the factory kind the contract stores and our table;
 *  3. that factory returns exactly this pool for its tokens (and fee): getPool(t0, t1, fee) or getPair(t0, t1).
 * Anything else is rejected: a contract can claim any factory(), but only the real factory registers its own pools.
 */
export function verifyPool(r: PoolReads, rules: FactoryRules): VerifyResult {
  if (!r.factory || !r.token0 || !r.token1 || !ADDRESS_RE.test(r.factory) || !ADDRESS_RE.test(r.token0) || !ADDRESS_RE.test(r.token1)) {
    return { ok: false, reason: 'not_a_pool' }
  }
  if (lc(r.token0) === ZERO || lc(r.token1) === ZERO) return { ok: false, reason: 'not_a_pool' }
  const rule = rules[lc(r.factory)]
  if (!rule) return { ok: false, reason: 'unsupported_factory' }
  if (!rule.enabled) return { ok: false, reason: 'factory_disabled' }
  const poolKind: Kind = r.fee !== undefined ? 'V3' : 'V2'
  if (poolKind !== rule.kind || rule.chainKind !== rule.kind) return { ok: false, reason: 'kind_mismatch' }
  if (!r.registered || lc(r.registered) === ZERO || lc(r.registered) !== lc(r.pool)) return { ok: false, reason: 'not_registered' }
  return { ok: true, dex: rule.name, kind: rule.kind, factory: rule.factory, fee: r.fee ?? null, token0: r.token0 as Address, token1: r.token1 as Address }
}

// ---------------------------------------------------------------------------------------------- activity and ranking

export const MIN_ACTIVE_LIQUIDITY_USD = 1

/** Under $1 (or, with no USD value known, nothing in the pool) is "No liquidity": the only real warning. Such pools rank last. */
export const hasLiquidity = (p: { liquidityUsd: number | null; hasBalance: boolean }): boolean =>
  p.liquidityUsd !== null ? p.liquidityUsd >= MIN_ACTIVE_LIQUIDITY_USD : p.hasBalance

export type PoolToken = { address: Address; symbol: string }
export type PoolRow = {
  pool: Address
  dex: string
  kind: Kind
  factory: Address
  fee: number | null
  feeLabel: string | null
  token0: PoolToken
  token1: PoolToken
  /** tokens of the pool that may be the project token (major assets removed); two entries = the user must choose */
  projectOptions: Address[]
  /** the searched token when it is one of the pool's tokens: pre-selected in the "which token is the project token" question */
  preferredProject?: Address
  liquidityUsd: number | null
  liquiditySource: 'gecko' | 'subgraph' | 'chain' | null
  volume24hUsd: number | null
  /** trades in the last 24 h; null = unknown (no source answered) */
  txs24h: number | null
  /** unix seconds of the last trade when a subgraph knows it */
  lastTradeAt: number | null
  /** who produced the activity numbers: GeckoTerminal, our subgraphs, or swap logs counted on-chain */
  activitySource: 'gecko' | 'subgraph' | 'logs' | null
  /** the DEX's own "Active": the pool has liquidity in range right now (V3: liquidity() > 0 at the current tick; V2: both reserves > 0); null = could not be read */
  inRange: boolean | null
  /** liquidity under $1: the only real warning, and such pools rank last */
  noLiquidity: boolean
  /** GeckoTerminal's price, cap and change figures for the pool's base token; only when GeckoTerminal indexes the pool */
  market?: GeckoMarket
}

/** A row as the API sends it and the UI reads it: the pool plus the pair label (searched token first) and whether GeckoTerminal knows the pool. */
export type PoolView = PoolRow & { pair: string; geckoKnown: boolean }

export const projectOptionsOf = (t0: string, t1: string) => [t0, t1].filter((t) => !isMajorToken(t)) as Address[]

/**
 * Strongest first, on any exchange (owner decision 2026-09-30: no exchange is favoured, strength decides): pools with nothing in range or no liquidity
 * go last, then liquidity in USD (more first), then real trades in 24 h, then 24 h volume, then the smaller fee tier, then the address.
 * Nothing here looks at which exchange a pool belongs to. Stable and deterministic.
 */
export function rankPools<T extends Pick<PoolRow, 'inRange' | 'noLiquidity' | 'txs24h' | 'liquidityUsd' | 'volume24hUsd' | 'dex' | 'fee' | 'pool'>>(rows: T[]): T[] {
  return [...rows].sort(
    (a, b) =>
      Number(a.noLiquidity) - Number(b.noLiquidity) ||
      Number(b.inRange === true) - Number(a.inRange === true) ||
      (b.liquidityUsd ?? -1) - (a.liquidityUsd ?? -1) ||
      (b.txs24h ?? -1) - (a.txs24h ?? -1) ||
      (b.volume24hUsd ?? -1) - (a.volume24hUsd ?? -1) ||
      (a.fee ?? 0) - (b.fee ?? 0) ||
      lc(a.pool).localeCompare(lc(b.pool)),
  )
}

/** Rough USD liquidity of a pool GeckoTerminal does not index: priced sides only (V2 doubles a single priced side). */
export function estimateLiquidityUsd(
  sides: Array<{ balance?: bigint; decimals: number; priceUsd?: number }>,
  kind: Kind,
): number | null {
  const priced = sides.filter((s) => s.balance !== undefined && s.priceUsd !== undefined && s.priceUsd > 0)
  if (priced.length === 0) return null
  const sum = priced.reduce((acc, s) => acc + (Number(s.balance) / 10 ** s.decimals) * (s.priceUsd as number), 0)
  return kind === 'V2' && priced.length === 1 ? sum * 2 : sum
}

/** Query token first, then the order the pool stores them. Used by the server for stable output. */
export const pairLabel = (row: Pick<PoolRow, 'token0' | 'token1' | 'projectOptions'>, preferred?: string) => {
  const first = [row.token0, row.token1].find((t) => lc(t.address) === lc(preferred)) ?? [row.token0, row.token1].find((t) => row.projectOptions.includes(t.address)) ?? row.token0
  const second = first === row.token0 ? row.token1 : row.token0
  return `${first.symbol}/${second.symbol}`
}

/** "$3.14K", "$1.2M", "<$0.01"; null (nothing known) is shown by the caller. */
export const fmtUsd = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (n === 0) return '$0'
  if (n < 0.01) return '<$0.01'
  if (n < 1000) return `$${n.toFixed(2)}`
  return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n)}`
}
/** A subgraph answer is trusted for a ZERO only with a fresh head (<= 10 min behind) and no indexing errors; a positive count is always evidence. */
export const SUBGRAPH_MAX_LAG_S = 600
export function subgraphTrust(meta: { hasIndexingErrors?: boolean; block?: { timestamp?: number | string } } | null | undefined, nowSec: number) {
  const ts = Number(meta?.block?.timestamp)
  const fresh = Number.isFinite(ts) && ts > 0 && nowSec - ts <= SUBGRAPH_MAX_LAG_S
  const clean = meta?.hasIndexingErrors === false
  return { fresh, clean, trusted: fresh && clean }
}
export const tradesFromSwaps = (count: number, trusted: boolean): number | null => (count > 0 ? count : trusted ? 0 : null)

export const ago = (sec: number) => (sec < 3600 ? `${Math.max(1, Math.round(sec / 60))}m ago` : sec < 172_800 ? `${Math.round(sec / 3600)}h ago` : `${Math.round(sec / 86_400)}d ago`)
export type BadgeState = 'active' | 'inactive' | 'unknown'
/** The badge on every row, with the DEX's own meaning: Active = liquidity in range now, Inactive = none in range, Unknown = could not be read. */
export const liquidityBadge = (r: Pick<PoolRow, 'inRange'>): BadgeState => (r.inRange === true ? 'active' : r.inRange === false ? 'inactive' : 'unknown')

export type TradeState = 'trades' | 'none' | 'unknown'
/** Trading in the last 24 h, from real data only: trades (or volume) above zero, the data says zero, or no source answered. */
export const tradeState = (r: Pick<PoolRow, 'txs24h' | 'volume24hUsd'>): TradeState =>
  (r.txs24h ?? 0) > 0 || (r.volume24hUsd ?? 0) > 0 ? 'trades' : r.txs24h === 0 || r.volume24hUsd === 0 ? 'none' : 'unknown'

/** The trading line, separate from the badge: "N trades / 24h", "No trades in 24h · last trade 5d ago", "Trades unknown". */
export function tradeLabel(
  r: Pick<PoolRow, 'txs24h' | 'volume24hUsd' | 'lastTradeAt'>,
  nowSec = Date.now() / 1000,
): { text: string; tone: 'success' | 'muted' } {
  const state = tradeState(r)
  if (state === 'trades') {
    const n = r.txs24h ?? 0
    return { text: n > 0 ? `${n}${n >= 1000 ? '+' : ''} trade${n === 1 ? '' : 's'} / 24h` : 'Trades in 24h', tone: 'success' }
  }
  if (state === 'none') return { text: `No trades in 24h${r.lastTradeAt ? ` · last trade ${ago(nowSec - r.lastTradeAt)}` : ''}`, tone: 'muted' }
  return { text: 'Trades unknown', tone: 'muted' }
}
